import React from "react";
import { Card, CardContent } from "@/components/ui/card";
import { CheckCircle2, CircleAlert, MinusCircle } from "lucide-react";
import { STAGE_COLOR, NOT_TRACKED, band, stageBars } from "./sleepFields";

export const ImpactChips = ({ impacts }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="sleep-impact-chips">
    {(impacts || []).map((i) => {
      const Icon = !i.tracked ? MinusCircle : i.ok ? CheckCircle2 : CircleAlert;
      const tone = !i.tracked ? "text-muted-foreground" : i.ok ? "text-[#2f6b45]" : "text-[#8a6321]";
      return (
        <div
          key={i.key}
          className="rounded-xl border border-border bg-muted/30 px-4 py-3 flex items-start gap-3"
          data-testid={`sleep-impact-${i.key}`}
        >
          <Icon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${tone}`} />
          <div className="min-w-0">
            <p className="text-sm font-medium leading-tight">{i.label}</p>
            <p className="text-[11px] text-muted-foreground mt-0.5">{i.detail}</p>
          </div>
        </div>
      );
    })}
  </div>
);

export const StageTimeline = ({ stages }) => (
  <div className="space-y-3" data-testid="sleep-stage-timeline">
    {(stages || []).map((s) => (
      <div key={s.key} data-testid={`sleep-stage-${s.key}`}>
        <p className="text-xs mb-1.5">
          <span className="font-medium">{s.label}</span>
          <span className="text-muted-foreground">
            {" · "}{s.duration_text || NOT_TRACKED}
          </span>
        </p>
        <div className="relative h-5 rounded-md bg-muted/60 overflow-hidden">
          {stageBars(s).map((seg, idx) => (
            <span
              key={`${s.key}-${idx}`}
              className="absolute top-0 bottom-0 rounded-[3px]"
              style={{
                left: `${seg.start_pct}%`,
                width: `${seg.width_pct}%`,
                background: STAGE_COLOR[s.key] || "#999",
              }}
            />
          ))}
        </div>
      </div>
    ))}
  </div>
);

export const QualityRow = ({ metric }) => {
  const tone = metric.in_range === null
    ? { bg: "#f2f2ef", fg: "#666" }
    : metric.in_range
      ? { bg: "#e8f3ec", fg: "#2f6b45" }
      : { bg: "#fbecec", fg: "#8c3a3a" };

  return (
    <div
      className="rounded-xl border border-border bg-muted/30 px-4 py-3.5"
      data-testid={`sleep-quality-${metric.key}`}
    >
      <div className="flex items-start justify-between gap-3 mb-2.5">
        <p className="text-sm">
          <span className="font-medium">{metric.label}</span>
          {metric.value_text && <span className="text-muted-foreground"> · {metric.value_text}</span>}
        </p>
        <span
          className="px-2.5 py-1 rounded-full text-[10px] font-semibold uppercase tracking-wide flex-shrink-0"
          style={{ background: tone.bg, color: tone.fg }}
          data-testid={`sleep-quality-status-${metric.key}`}
        >
          {metric.status || "Not tracked"}
        </span>
      </div>

      {metric.tracked ? (
        <div className="relative h-5">
          <div className="absolute inset-x-0 top-1.5 h-2 rounded-full bg-muted" />
          <div
            className="absolute top-1.5 h-2 rounded-full"
            style={{
              left: `${metric.low_pct}%`,
              width: `${Math.max(2, metric.high_pct - metric.low_pct)}%`,
              background: "#cfe3d5",
              border: "1px dashed #7aa98c",
            }}
          />
          <div
            className="absolute top-0 w-5 h-5 rounded-full border-2 border-white shadow"
            style={{
              left: `calc(${metric.value_pct}% - 10px)`,
              background: metric.in_range ? "#4b8f68" : "#b4584f",
            }}
            data-testid={`sleep-quality-marker-${metric.key}`}
          />
        </div>
      ) : (
        <p className="text-[11px] text-muted-foreground">{NOT_TRACKED}</p>
      )}
    </div>
  );
};

export const ScoreHeader = ({ night }) => {
  const tone = band(night?.score_label);
  return (
    <div data-testid="sleep-score-block">
      <div className="flex items-baseline gap-3">
        <span
          className="font-editorial text-6xl sm:text-7xl leading-none"
          style={{ color: tone.ring }}
          data-testid="sleep-score-value"
        >
          {night?.score ?? "—"}
        </span>
        <span className="text-lg font-medium" data-testid="sleep-score-label">
          {night?.score_label || NOT_TRACKED}
        </span>
      </div>
      <p className="text-sm text-muted-foreground mt-2 max-w-xl" data-testid="sleep-score-note">
        {night?.score_note}
      </p>
    </div>
  );
};

export const MoreAboutSleep = ({ more }) => (
  <Card className="rounded-2xl" data-testid="sleep-more-card">
    <CardContent className="p-5 grid grid-cols-1 sm:grid-cols-3 gap-3">
      {(more || []).map((m) => (
        <div
          key={m.key}
          className="rounded-xl border border-border bg-muted/30 px-4 py-3"
          data-testid={`sleep-more-${m.key}`}
        >
          <p className="text-sm font-medium">{m.label}</p>
          <p className="text-xs text-muted-foreground mt-0.5">{m.value_text || NOT_TRACKED}</p>
        </div>
      ))}
    </CardContent>
  </Card>
);
